import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Check } from "lucide-react";
import { COMPANY, STATS, PHOTO_HOUSE, PHOTO_WORK } from "@/data/site";

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
};

const PERKS = [
  "Familienbetrieb mit Handschlag-Qualität",
  "Moderne Reinigungstechnik & Ausrüstung",
  "Für private und gewerbliche Kunden",
  "Pünktlich, gründlich und zuverlässig",
];

export const About = () => {
  return (
    <section id="ueber-uns" className="relative overflow-hidden bg-white py-24 md:py-32" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="relative lg:col-span-5"
          >
            <div className="overflow-hidden rounded-[2rem] shadow-2xl">
              <img
                src={PHOTO_HOUSE}
                alt="Kilic Gebäudeservice Einsatzfahrzeug vor einem gereinigten Wohnhaus"
                className="h-[26rem] w-full object-cover"
              />
            </div>
            <div className="animate-floaty absolute -bottom-8 -right-4 w-40 overflow-hidden rounded-2xl border-4 border-white shadow-xl sm:w-52">
              <img
                src={PHOTO_WORK}
                alt="Fassadenreinigung mit Teleskopstange im Einsatz"
                className="h-40 w-full object-cover sm:h-56"
              />
            </div>
            <div className="absolute -left-4 -top-6 rounded-2xl bg-[var(--kilic-blue)] px-6 py-4 text-white shadow-xl">
              <div className="font-display text-3xl font-extrabold leading-none">
                {COMPANY.years}+
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider text-white/80">
                Jahre Erfahrung
              </div>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 lg:pl-6"
          >
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[var(--kilic-green-dark)]">
              Über uns
            </span>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tighter text-neutral-900 sm:text-4xl md:text-5xl">
              Sauberkeit aus{" "}
              <span className="text-gradient">Familienhand</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-neutral-600 sm:text-base md:text-lg">
              Mit über {COMPANY.years} Jahren Erfahrung in der Reinigungsbranche
              steht Kilic Gebäudeservice für professionelle und zuverlässige
              Dienstleistungen auf höchstem Niveau. Wir verbinden langjährige
              Erfahrung mit modernster Technik und einem erfahrenen Team.
            </p>
            <p className="mt-4 text-base leading-relaxed text-neutral-600">
              Ob glasklare Fenster, saubere Dachrinnen, gepflegte Fassaden oder
              Ihr Garten – wir sorgen dafür, dass Ihre Immobilie immer in bestem
              Licht erstrahlt. Vertrauen Sie auf unsere Expertise.
            </p>

            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--kilic-green)] text-white">
                    <Check className="h-4 w-4" strokeWidth={3} />
                  </span>
                  <span className="text-sm font-medium text-neutral-700">
                    {perk}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-20 grid grid-cols-2 gap-6 rounded-[2rem] border border-black/5 bg-[#fafafa] p-8 md:grid-cols-4 md:p-10"
          data-testid="stats-grid"
        >
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-4xl font-extrabold text-[var(--kilic-blue)] md:text-5xl">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-2 text-xs font-semibold uppercase tracking-wider text-neutral-500 md:text-sm">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
